import styled from "@emotion/styled";
import memoizeOne from "memoize-one";
import { PureComponent } from "react";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import { Filter, IDateRangeWithCompareFilter, IFilters } from "constants/filters";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { trackEngagement } from "helpers/analytics";
import { DetailedSource } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import { ITarget } from "store/entities/application-settings/interfaces";
import { ITag } from "store/entities/tags";
import { ITeam } from "store/entities/teams";
import { withPx } from "themes/helpers";

import { FiltersMenu } from "./FiltersMenu";
import { ReadOnlyFilter } from "./ReadOnlyFilter";
import ActionFilter from "./action-filter/ActionFilter";
import { AgentAssignmentFilter } from "./agent-assignment-filter";
import { AgentFilter } from "./agent-filter";
import AuthorFilter from "./agent-filter/AuthorFilter";
import { AttachmentsFilter } from "./attachments-filter";
import { DateRangeFilter } from "./date-range-filter";
import { FollowersFilter } from "./followers-filter";
import { GroupFilter } from "./group-filter";
import { IAvailableFilter, IFilterBaseProps, IFilterConfig, IFilterUpdate } from "./interfaces";
import LanguageFilter from "./language-filter/LanguageFilter";
import PriorityFilter from "./priority-filter/PriorityFilter";
import { RatingFilter } from "./rating-filter";
import { TagFilter } from "./tag-filter";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-cycle
import TargetFilter from "./target-filter/TargetFilter";
import { TicketSourceFilter } from "./ticket-source-filter";
import { TicketStatusFilter } from "./ticket-status-filter";

const Container = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  background-color: var(${DesignToken.SurfacePrimaryDefault});
  gap: var(${SpacingToken.Spacing2});
  @media (min-width: ${withPx(ResponsiveBreakpoint.Mobile)}) {
    flex-wrap: wrap;
  }
`;

interface IProps {
  availableFilters: IAvailableFilter[];
  disabledFilters?: Filter[];
  filters: IFilters;
  compareFilters?: IFilters;
  filtersOrder: Filter[];
  currentAgent?: IAgent;
  agents: IAgent[];
  groups: ITeam[];
  allGroups: ITeam[];
  tags: ITag[];
  allTags?: ITag[];
  eventPlace?: string;
  onFilterOpen?(): void;
  onFilterClose?(): void;
  onFilterUpdate(filterUpdate: IFilterUpdate): void;
  isReports?: boolean;
  targets?: Record<string, ITarget>;
  languageLabels: Record<string, string>;
  availableSources: DetailedSource[];
}

interface IState {
  currentFilter: Filter | null;
  isMenuVisible: boolean;
  filtersDrafts: Partial<IFilters>;
  filtersCompareDrafts: Partial<IFilters>;
}

function calculateOrderedFilters(availableFilters: IAvailableFilter[]): Filter[] {
  return availableFilters.filter(filter => !filter.readOnly).map((filter: IAvailableFilter) => filter.filter);
}

const getOrderedFilters = memoizeOne((availableFilters: IAvailableFilter[]): Filter[] =>
  calculateOrderedFilters(availableFilters)
);

function calculateFilterConfigs(availableFilters: IAvailableFilter[]): { [key in Filter]?: IFilterConfig } {
  return availableFilters.reduce(
    (acc, filter: IAvailableFilter) => ({
      ...acc,
      [filter.filter]: filter.config,
    }),
    {}
  );
}

const getFilterConfigs = memoizeOne((availableFilters: IAvailableFilter[]): { [key in Filter]?: IFilterConfig } =>
  calculateFilterConfigs(availableFilters)
);

const initialState: IState = {
  currentFilter: null,
  isMenuVisible: false,
  filtersDrafts: {
    // @ts-expect-error TS(2418)
    [Filter.CreationDateRange]: null,
    // @ts-expect-error TS(2418)
    [Filter.LastActivityDateRange]: null,
    // @ts-expect-error TS(2418)
    [Filter.DateRange]: null,
  },
  filtersCompareDrafts: {
    // @ts-expect-error TS(2418)
    [Filter.CreationDateRange]: null,
    // @ts-expect-error TS(2418)
    [Filter.LastActivityDateRange]: null,
    // @ts-expect-error TS(2418)
    [Filter.DateRange]: null,
  },
};

const calculateVisibleFilters = memoizeOne(
  (availableFilters: IAvailableFilter[], filtersOrder: Filter[], currentFilter: Filter): string[] => {
    let filtersToShow;
    if (currentFilter && !filtersOrder.some(filter => filter === currentFilter)) {
      filtersToShow = [currentFilter, ...filtersOrder];
    } else {
      filtersToShow = filtersOrder;
    }
    return filtersToShow.filter(filterToShow =>
      availableFilters.some(availableFilter => availableFilter.filter === filterToShow)
    );
  }
);

export class Filters extends PureComponent<IProps, IState> {
  static defaultProps = {
    filtersOrder: [],
    compareFilters: {},
  };

  state: IState = { ...initialState };

  private get visibleFiltersNames(): string[] {
    // @ts-expect-error TS(2345)
    return calculateVisibleFilters(this.props.availableFilters, this.props.filtersOrder, this.state.currentFilter);
  }

  getCommonFilterProps = <F extends Filter>(
    filterName: F,
    filterConfig: IFilterConfig,
    items?: unknown[] | Record<string, unknown>
  ): IFilterBaseProps<F> => {
    // @ts-expect-error TS(2322)
    const filterValues: IFilters[F] = this.state.filtersDrafts[filterName] || this.props.filters[filterName] || null;
    // @ts-expect-error TS(2322)
    const filterCompareValues: IFilters[F] =
      // @ts-expect-error TS(2532)
      this.state.filtersCompareDrafts[filterName] || this.props.compareFilters[filterName] || null;

    return {
      key: filterName,
      filterName,
      isOpen: this.state.currentFilter === filterName,
      filterConfig,
      filterValues,
      filterCompareValues,
      // @ts-expect-error TS(2345)
      filterLabelValues: filterConfig.getFilterLabelValues(filterValues, items, filterCompareValues),
      // @ts-expect-error TS(2322)
      onChange: this.handleFilterUpdate,
      onClose: this.handleFilterClose,
      onOpen: this.handleFilterOpen,
      onRemove: this.handleFilterRemove,
    };
  };

  handleMenuItemSelect = (type: Filter): void => {
    this.setState({
      currentFilter: type,
      isMenuVisible: false,
    });
  };

  handleFilterUpdate = (filterUpdate: IFilterUpdate): void => {
    this.handleFilterDraftUpdate(filterUpdate);

    const isAddingFilter = !this.props.filters[filterUpdate.name];

    if (!filterUpdate.isDraft) {
      this.props.onFilterUpdate({ ...filterUpdate, applyState: isAddingFilter ? "added" : "updated" });
    }
  };

  handleFilterDraftUpdate = (filterUpdate: IFilterUpdate): void => {
    if (
      [Filter.CreationDateRange, Filter.DateRange, Filter.LastActivityDateRange, Filter.LastMessageDateRange].includes(
        filterUpdate.name
      )
    ) {
      this.setState(({ filtersDrafts, filtersCompareDrafts }) => ({
        filtersDrafts: {
          ...filtersDrafts,
          [filterUpdate.name]: filterUpdate.value,
        },
        filtersCompareDrafts: {
          ...filtersCompareDrafts,
          [filterUpdate.name]: filterUpdate.compare as IDateRangeWithCompareFilter,
        },
      }));
    }
  };

  handleFilterRemove = (filterType: Filter): void => {
    this.props.onFilterUpdate({ name: filterType, value: null, applyState: "deleted" });
    this.setState({
      currentFilter: null,
    });
  };

  handleRatingDateRangeFilterRemove = (): void => this.handleFilterRemove(Filter.RatingDateRange);
  handleResolutionDateRangeFilterRemove = (): void => this.handleFilterRemove(Filter.ResolutionDateRange);
  handleResponseDateRangeFilterRemove = (): void => this.handleFilterRemove(Filter.ResponseDateRange);
  handleResponseAuthorFilterRemove = (): void => this.handleFilterRemove(Filter.ResponseAuthor);

  handleFilterClose = (): void => {
    if (this.props.onFilterClose) {
      this.props.onFilterClose();
    }
    this.setState({
      currentFilter: null,
      filtersDrafts: { ...initialState.filtersDrafts },
      filtersCompareDrafts: { ...initialState.filtersCompareDrafts },
    });
  };

  handleFilterOpen = (filterType: Filter): void => {
    if (this.props.onFilterOpen) {
      this.props.onFilterOpen();
    }
    this.setState({
      currentFilter: filterType,
    });
  };

  handleFiltersMenuOpen = (): void => {
    if (this.props.onFilterOpen) {
      this.props.onFilterOpen();
    }
    this.setState({
      isMenuVisible: true,
    });
    const { eventPlace } = this.props;
    if (eventPlace) {
      trackEngagement(`${eventPlace}: Add filter button clicked`, eventPlace);
    }
  };

  handleFiltersMenuOpenShortcut = (): void => {
    if (this.props.onFilterOpen) {
      this.props.onFilterOpen();
    }
    this.setState({
      isMenuVisible: true,
    });
    const { eventPlace } = this.props;
    if (eventPlace) {
      trackEngagement("Used shortcut", eventPlace, { shortcut: "Filters opened" });
    }
  };

  handleFiltersMenuClose = (): void => {
    if (this.props.onFilterClose) {
      this.props.onFilterClose();
    }
    this.setState({
      isMenuVisible: false,
    });
  };

  render(): JSX.Element {
    const {
      agents,
      availableFilters,
      groups,
      tags,
      allGroups,
      allTags,
      currentAgent,
      isReports = false,
      targets,
      languageLabels,
    } = this.props;

    const { isMenuVisible } = this.state;
    const filterConfigs = getFilterConfigs(availableFilters);

    return (
      <Container>
        <FiltersMenu
          chosenFilters={this.props.filtersOrder}
          filters={getOrderedFilters(availableFilters)}
          disabledFilters={this.props.disabledFilters}
          filterConfigs={filterConfigs}
          isOpen={isMenuVisible}
          onItemSelect={this.handleMenuItemSelect}
          onClose={this.handleFiltersMenuClose}
          onOpen={this.handleFiltersMenuOpen}
        />
        {this.visibleFiltersNames.map(filterName => {
          // @ts-expect-error TS(7053)
          const filterConfig = filterConfigs[filterName];
          const filterValues: IFilters[] =
            // @ts-expect-error TS(7053)
            this.state.filtersDrafts[filterName] || this.props.filters[filterName] || null;

          switch (filterName) {
            case Filter.Agent: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, agents);
              return (
                <AgentFilter
                  {...commonProps}
                  // @ts-expect-error TS(2322)
                  agents={agents}
                  currentAgent={currentAgent}
                  multiple={filterConfig.multiple}
                />
              );
            }

            case Filter.Group: {
              const commonProps = {
                ...this.getCommonFilterProps(filterName, filterConfig, groups),
                filterLabelValues: filterConfig.getFilterLabelValues(filterValues, groups, undefined, allGroups),
              };

              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              return <GroupFilter multiple={filterConfig.multiple} {...commonProps} groups={groups} />;
            }

            case Filter.Rating: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, filterConfig.filterLabels);
              return <RatingFilter {...commonProps} />;
            }

            case Filter.Tag: {
              const commonProps = {
                ...this.getCommonFilterProps(filterName, filterConfig, groups),
                filterLabelValues: filterConfig.getFilterLabelValues(filterValues, tags, undefined, allTags),
              };

              // @ts-expect-error TS(2532): Object is possibly 'undefined'.
              return <TagFilter {...commonProps} tags={tags} multiple={filterConfig.multiple} />;
            }

            case Filter.AgentAssignment: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig);
              return <AgentAssignmentFilter {...commonProps} />;
            }

            case Filter.DateRange:
            case Filter.CreationDateRange:
            case Filter.LastActivityDateRange:
            case Filter.LastMessageDateRange: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig);

              // @ts-expect-error TS(2322)
              return <DateRangeFilter {...commonProps} skipCustomPeriodOption={isReports} />;
            }

            case Filter.TicketStatus: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, filterConfig.filterLabels);
              return <TicketStatusFilter {...commonProps} />;
            }

            case Filter.DetailedSource: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, filterConfig.filterLabels);
              return <TicketSourceFilter {...commonProps} />;
            }

            case Filter.Attachments: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, filterConfig.filterLabels);
              return <AttachmentsFilter {...commonProps} />;
            }

            case Filter.RatingDateRange: {
              const filterValues = this.props.filters[filterName] || null;
              const { icon, getFilterLabelValues, title } = filterConfig;
              return (
                <ReadOnlyFilter
                  key={filterName}
                  icon={icon}
                  label={getFilterLabelValues(filterValues)}
                  onRemove={this.handleRatingDateRangeFilterRemove}
                  title={title}
                  wide
                />
              );
            }

            case Filter.ResolutionDateRange: {
              const filterValues = this.props.filters[filterName] || null;
              const { icon, getFilterLabelValues, title } = filterConfig;
              return (
                <ReadOnlyFilter
                  key={filterName}
                  icon={icon}
                  label={getFilterLabelValues(filterValues)}
                  onRemove={this.handleResolutionDateRangeFilterRemove}
                  title={title}
                  wide
                />
              );
            }

            case Filter.ResponseDateRange: {
              const filterValues = this.props.filters[filterName] || null;
              const { icon, getFilterLabelValues, title } = filterConfig;
              return (
                <ReadOnlyFilter
                  key={filterName}
                  icon={icon}
                  label={getFilterLabelValues(filterValues)}
                  onRemove={this.handleResponseDateRangeFilterRemove}
                  title={title}
                  wide
                />
              );
            }

            case Filter.ResponseAuthor: {
              const filterValues = this.props.filters[filterName] || null;
              const { icon, getFilterLabelValues, title } = filterConfig;
              return (
                <ReadOnlyFilter
                  key={filterName}
                  icon={icon}
                  label={getFilterLabelValues(filterValues, agents)}
                  onRemove={this.handleResponseAuthorFilterRemove}
                  title={title}
                  wide
                />
              );
            }

            case Filter.Priority: {
              const props = this.getCommonFilterProps(filterName, filterConfig, []);

              return <PriorityFilter {...props} />;
            }

            case Filter.Followers: {
              const props = this.getCommonFilterProps(filterName, filterConfig, agents);
              // @ts-expect-error TS(2322)
              return <FollowersFilter {...props} agents={agents} currentAgent={currentAgent} />;
            }

            case Filter.Action: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, filterConfig.filterLabels);
              return <ActionFilter {...commonProps} />;
            }

            case Filter.Target: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, targets);
              return <TargetFilter {...commonProps} />;
            }
            case Filter.Author: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, agents);
              return (
                <AuthorFilter
                  {...commonProps}
                  // @ts-expect-error TS(2322): Type '{ agents: IAgent[]; currentAgent: any; filte... Remove this comment to see the full error message
                  agents={agents}
                  currentAgent={currentAgent}
                  multiple={filterConfig.multiple}
                />
              );
            }

            case Filter.Language: {
              const commonProps = this.getCommonFilterProps(filterName, filterConfig, languageLabels);
              return <LanguageFilter {...commonProps} />;
            }

            default:
              return null;
          }
        })}
      </Container>
    );
  }
}
